import { Pencil, Receipt, RefreshCw, Check, AlertTriangle } from "lucide-react";
import clsx from "clsx";
import Button from "../../../../components/Button";
import {
  BillingReconciliation,
  BILLING_TYPES,
  BILLING_TYPE_LABELS,
  ClientBilling,
  isCommissionBillingType,
  isPlatformFeeBillingType,
} from "../../types";
import { formatCommissionRate } from "../../utils";
import { AddonDraft, BillingConfigDraft } from "./billing-config-draft";
import { Card, ConfigItem, AddonEditor } from "./primitives";

// 0–1 rate → display percent.
const pctRate = (r: number | null | undefined) =>
  r == null ? "—" : `${(r * 100).toFixed(1)}%`;

// Statuses that block an auto-apply — surfaced for manual handling.
const RECONCILE_REVIEW_MESSAGES: Record<string, string> = {
  needs_review:
    "Needs manual review — Stripe has a non-percentage or multiple discounts. Set the rate manually with Edit.",
  stripe_error: "Couldn't read Stripe right now. Try Sync again.",
  no_stripe_customer: "No Stripe customer is linked to this user.",
  no_billing_config: "No billing config yet — set a rate with Edit first.",
};

interface BillingConfigCardProps {
  client: ClientBilling;
  isEditing: boolean;
  draft: BillingConfigDraft;
  onDraftChange: (patch: Partial<BillingConfigDraft>) => void;
  onAddonChange: (type: string, patch: Partial<AddonDraft>) => void;
  isSaving: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  reconciliation: BillingReconciliation | null;
  onSync: () => void;
  isSyncing: boolean;
  onApplyStripeRate: () => void;
  isApplying: boolean;
}

export function BillingConfigCard({
  client,
  isEditing,
  draft,
  onDraftChange,
  onAddonChange,
  isSaving,
  onEdit,
  onSave,
  onCancel,
  reconciliation,
  onSync,
  isSyncing,
  onApplyStripeRate,
  isApplying,
}: BillingConfigCardProps) {
  const enabledAddons = (client.addons ?? []).filter((a) => a.enabled);

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Receipt className="w-4.5 h-4.5 text-content-tertiary" />
          <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
            Billing Config
          </span>
        </div>
        {!isEditing && (
          <div className="flex items-center gap-3">
            <button
              onClick={onSync}
              disabled={isSyncing}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary-hover font-medium transition-colors disabled:opacity-50"
            >
              <RefreshCw className={clsx("w-3 h-3", isSyncing && "animate-spin")} />
              {isSyncing ? "Checking…" : "Sync with Stripe"}
            </button>
            <button
              onClick={onEdit}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary-hover font-medium transition-colors"
            >
              <Pencil className="w-3 h-3" />
              Edit
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3 pt-1">
          {/* Billing Type selector */}
          <div>
            <label className="block text-xs font-medium text-content-tertiary mb-1">
              Billing Type
            </label>
            <select
              value={draft.billingType}
              onChange={(e) => onDraftChange({ billingType: e.target.value as BillingConfigDraft["billingType"] })}
              className="w-full px-3 py-2 text-sm border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-surface-primary"
              disabled={isSaving}
            >
              {BILLING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {BILLING_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          {/* Minimum fee — commission_or_flat only. Recorded, NOT charged:
              the helper text has to say so or an admin will assume the
              floor is already being enforced on the invoice. */}
          {draft.billingType === "commission_or_flat" && (
            <div>
              <label className="block text-xs font-medium text-content-tertiary mb-1">
                Minimum Monthly Fee ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="50000"
                value={draft.minimumFeeAmount}
                onChange={(e) => onDraftChange({ minimumFeeAmount: e.target.value })}
                placeholder="329"
                className="w-full px-3 py-2 text-sm border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-surface-primary"
                disabled={isSaving}
              />
              <p className="mt-1 text-xs text-amber-700">
                Required. Recorded for reference only — invoices still
                charge the commission alone. The floor is not enforced yet.
              </p>
            </div>
          )}

          {/* Platform Fee fields — shown for fixed and hybrid */}
          {isPlatformFeeBillingType(draft.billingType) && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-content-tertiary mb-1">
                  Platform Fee Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="50000"
                  value={draft.platformFeeAmount}
                  onChange={(e) => onDraftChange({ platformFeeAmount: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-surface-primary"
                  disabled={isSaving}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-content-tertiary mb-1">
                  Platform Fee Description
                </label>
                <input
                  type="text"
                  maxLength={255}
                  value={draft.platformFeeDescription}
                  onChange={(e) => onDraftChange({ platformFeeDescription: e.target.value })}
                  placeholder="e.g. Pricing & Revenue Management"
                  className="w-full px-3 py-2 text-sm border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-surface-primary"
                  disabled={isSaving}
                />
              </div>
            </div>
          )}

          {/* Commission Rate + Markup row — every commission-billed type,
              commission_or_flat included (it is charged on this rate). */}
          {isCommissionBillingType(draft.billingType) && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-content-tertiary mb-1">
                Commission Rate
              </label>
              <div className="flex items-center">
                <input
                  type="number"
                  value={draft.rate}
                  onChange={(e) => onDraftChange({ rate: e.target.value })}
                  placeholder="15"
                  className="w-full px-3 py-2 text-sm border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-surface-primary"
                  min="0"
                  max="100"
                  step="0.5"
                  autoFocus
                  disabled={isSaving}
                />
                <span className="text-content-tertiary text-sm ml-1.5">
                  %
                </span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-content-tertiary mb-1">
                Display Markup
              </label>
              <div className="flex items-center">
                <input
                  type="number"
                  value={draft.markup}
                  onChange={(e) => onDraftChange({ markup: e.target.value })}
                  placeholder="4"
                  className="w-full px-3 py-2 text-sm border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-surface-primary"
                  min="0"
                  step="0.5"
                  disabled={isSaving}
                />
                <span className="text-content-tertiary text-sm ml-1.5">
                  %
                </span>
              </div>
            </div>
          </div>
          )}

          {/* Onboarding fee toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              className={clsx(
                "w-8 h-[18px] rounded-full relative transition-colors",
                draft.onboardingFee
                  ? "bg-primary"
                  : "bg-content-disabled",
              )}
              onClick={() =>
                !isSaving &&
                onDraftChange({ onboardingFee: !draft.onboardingFee })
              }
            >
              <div
                className={clsx(
                  "absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-transform",
                  draft.onboardingFee
                    ? "translate-x-[16px]"
                    : "translate-x-[2px]",
                )}
              />
            </div>
            <span className="text-sm text-content-secondary">
              Show onboarding fee on invoice
            </span>
          </label>

          {/* Reputation Management waiver toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              className={clsx(
                "w-8 h-[18px] rounded-full relative transition-colors",
                draft.repMgmtWaiver
                  ? "bg-primary"
                  : "bg-content-disabled",
              )}
              onClick={() =>
                !isSaving &&
                onDraftChange({ repMgmtWaiver: !draft.repMgmtWaiver })
              }
            >
              <div
                className={clsx(
                  "absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-transform",
                  draft.repMgmtWaiver
                    ? "translate-x-[16px]"
                    : "translate-x-[2px]",
                )}
              />
            </div>
            <span className="text-sm text-content-secondary">
              Show Reputation Management waiver line
            </span>
          </label>

          {/* Add-ons */}
          <div className="pt-3 border-t border-border-secondary space-y-4">
            <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
              Add-ons
            </span>
            {draft.addons.map((a) => (
              <AddonEditor
                key={a.type}
                draft={a}
                disabled={isSaving}
                onChange={(patch) => onAddonChange(a.type, patch)}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2 border-t border-border-secondary">
            <Button
              onClick={() => onSave()}
              disabled={isSaving}
              className="!py-2 !px-5 !text-sm !rounded-lg"
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="py-2 px-4 text-sm text-content-tertiary hover:text-content-primary hover:bg-surface-secondary rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 pt-2">
          <ConfigItem
            label="Rate"
            value={`${formatCommissionRate(
              reconciliation && reconciliation.appCommissionRate != null
                ? reconciliation.appCommissionRate * 100
                : client.commissionRate,
            )}%`}
          />
          <ConfigItem
            label="Markup"
            value={
              client.displayCommissionMarkup != null
                ? `${(client.displayCommissionMarkup * 100).toFixed(1)}%`
                : "4%"
            }
          />
          <div className="col-span-2">
            <div className="text-xs text-content-tertiary uppercase tracking-wider mb-1">
              Add-ons
            </div>
            {enabledAddons.length === 0 ? (
              <div className="text-base font-medium text-content-primary">
                None
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {enabledAddons.map((addon) => {
                  const trialEnd = addon.trialEndDate
                    ? new Date(addon.trialEndDate + "T00:00:00")
                    : null;
                  const trialEnded =
                    trialEnd != null && trialEnd.getTime() <= Date.now();
                  return (
                    <div key={addon.type}>
                      <div className="text-base font-medium text-content-primary">
                        {addon.displayName}
                        {addon.monthlyPrice != null && (
                          <span className="text-content-secondary">
                            {" — $"}
                            {addon.monthlyPrice.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                            /mo
                          </span>
                        )}
                      </div>
                      {addon.trialEnabled ? (
                        <div className="text-xs font-medium text-blue-700 mt-0.5">
                          30-day free trial —{" "}
                          {trialEnd
                            ? `${
                                trialEnded ? "ended" : "ends"
                              } ${trialEnd.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}`
                            : "start date not set"}
                        </div>
                      ) : (
                        <div className="text-xs text-content-tertiary mt-0.5">
                          No free trial
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {!isEditing && reconciliation && (
        <div className="mt-3 pt-3 border-t border-border-secondary">
          {reconciliation.status === "in_sync" && (
            <div className="flex items-center gap-2 text-xs text-success">
              <Check className="w-3.5 h-3.5 shrink-0" />
              <span>
                App rate matches Stripe
                {reconciliation.stripeDiscountRates.length
                  ? ` (${pctRate(reconciliation.stripeDiscountRates[0])})`
                  : " (no Stripe discount)"}
                .
              </span>
            </div>
          )}

          {reconciliation.status === "drift" && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-warning-dark font-medium">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                <span>Drift detected — Stripe differs from the app.</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-content-secondary">
                <span>
                  App:{" "}
                  <span className="font-semibold text-content-primary">
                    {pctRate(reconciliation.appCommissionRate)}
                  </span>
                </span>
                <span>→</span>
                <span>
                  Stripe:{" "}
                  <span className="font-semibold text-content-primary">
                    {pctRate(reconciliation.suggestedRate)}
                  </span>
                </span>
              </div>
              <button
                onClick={onApplyStripeRate}
                disabled={isApplying}
                className="flex items-center gap-1 text-xs text-white bg-primary hover:bg-primary-hover rounded-md px-2.5 py-1.5 font-medium disabled:opacity-50"
              >
                <RefreshCw className={clsx("w-3 h-3", isApplying && "animate-spin")} />
                {isApplying
                  ? "Applying…"
                  : `Apply Stripe rate (${pctRate(reconciliation.suggestedRate)})`}
              </button>
            </div>
          )}

          {RECONCILE_REVIEW_MESSAGES[reconciliation.status] && (
            <div className="flex items-start gap-2 text-xs text-content-secondary">
              <AlertTriangle className="w-3.5 h-3.5 text-warning-dark mt-0.5 shrink-0" />
              <span>{RECONCILE_REVIEW_MESSAGES[reconciliation.status]}</span>
            </div>
          )}

          {reconciliation.warnings.length > 0 && (
            <ul className="mt-2 space-y-1">
              {reconciliation.warnings.map((w, i) => (
                <li key={i} className="text-[11px] text-content-tertiary">
                  • {w}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
